import React, { useCallback } from 'react';

// Types
import { WidthType } from '../../components/dropdown';

// Hooks
import { useTranslation } from '../../../shared/utils/hooks/use-translation';

// Components
import WpSelect from '../select/WpSelectV2';
import { WpAvatar, WpAvatarEmpty } from '../avatar';
import WpLabelled from '../labelled/WpLabelled';

import './WpUserInput.scss';

export type WpUserInputProps = {
  /** ID for the input */
  id: string;
  /** Label for the input */
  label: string;
  /** Hide the label, but keep on screen for screen readers */
  labelHidden?: boolean;
  /** Hint text to display */
  placeholder?: string;
  /** Initial value for the input */
  value?: string | number;
  required?: boolean;
  /** Error to display beneath the label */
  error?: string;
  users?: Array<any>;
  onChange?: Function;
  handleRemoveUser?: Function;
  removeUserLabel?: string;
  searchPlaceholder?: string;
  showSearch?: boolean;
  disabled?: boolean;
  showRequiredIndicator?: boolean;
  components?: {
    innerDropdown?: Function;
    dropdownFooter?: Function;
  };
  optionsWidth?: WidthType;
  addRemoveUserOption?: boolean;
  forceReset?: boolean;
  resetOnSelect?: boolean;
};

const WpUserInput = ({
  users,
  id,
  label,
  onChange,
  error,
  required = false,
  placeholder,
  handleRemoveUser,
  labelHidden,
  removeUserLabel = 'Remove User',
  showSearch = true,
  disabled = false,
  addRemoveUserOption = false,
  value,
  searchPlaceholder = 'Search users',
  components,
  optionsWidth = WidthType.fitWidth,
  showRequiredIndicator,
  forceReset,
  resetOnSelect,
}: WpUserInputProps) => {
  const { tr } = useTranslation();
  const hasCustomInnerDropdown = Boolean(components?.innerDropdown);
  const baseClassName = 'WpUserInput';

  const translatedRemoveUserLabel = removeUserLabel || tr('perform.design_system.user_input.remove_user');
  const translatedSearchPlaceholder = searchPlaceholder || tr('perform.design_system.user_input.search_users');

  const removeUserOption =
    (addRemoveUserOption || handleRemoveUser) && value
      ? [
          {
            label: translatedRemoveUserLabel,
            value: 'user-input-remove-user',
            hideLabel: true,
          },
        ]
      : [];

  const formattedOptions = [
    ...removeUserOption,
    ...users.map(user => {
      return {
        ...user,
        label: user.fullName || user.displayName || user.email,
        value: user.id,
      };
    }),
  ];

  const handleUserSelectClick = useCallback(
    selected => {
      if (selected.value === 'user-input-remove-user') {
        if (handleRemoveUser) handleRemoveUser();
        else onChange(null);
      } else {
        onChange(selected);
      }
    },
    [onChange, handleRemoveUser]
  );

  const customUserPickerSelectOption = ({ option }) => {
    if (option.value === 'user-input-remove-user') {
      return (
        <div className={`${baseClassName}__select-options__remove`}>
          <div className={`${baseClassName}__select-options__remove__icon`}>
            <WpAvatarEmpty size="sm" />
          </div>
          <span className={`${baseClassName}__select-options__label`}>{option.label}</span>
        </div>
      );
    }

    return (
      <div className={`${baseClassName}__select-options`}>
        {option?.avatar ? <WpAvatar avatar={option.avatar} size="sm" /> : <WpAvatarEmpty size="sm" />}
        <span className={`${baseClassName}__select-options__label`}>{option.label}</span>
      </div>
    );
  };

  const customInnerMarkup = ({ selected }) => {
    const CustomInner = components?.innerDropdown;
    const data = !selected || selected.value === 'user-input-remove-user' ? null : selected;
    if (CustomInner) {
      return (
        <WpLabelled id={id} label={label} labelHidden={labelHidden} showRequiredIndicator={required}>
          <CustomInner user={data} />
        </WpLabelled>
      );
    }

    return null;
  };
  return (
    <WpSelect
      id={id}
      label={label}
      placeholder={placeholder}
      labelHidden={labelHidden}
      isSearchable={showSearch}
      options={formattedOptions}
      error={error}
      required={required}
      value={value}
      searchPlaceholder={translatedSearchPlaceholder}
      components={{
        option: customUserPickerSelectOption,
        innerDropdown: hasCustomInnerDropdown ? customInnerMarkup : null,
        dropdownFooter: components?.dropdownFooter,
      }}
      onChange={handleUserSelectClick}
      disabled={disabled}
      optionsWidth={optionsWidth}
      showRequiredIndicator={showRequiredIndicator}
      forceReset={forceReset}
      resetOnSelect={resetOnSelect}
    />
  );
};

export default WpUserInput;
export { WidthType };
